import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Save as SaveIcon, Restore as RestoreIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmDialog from '../ConfirmDialog';

export default function SettingsTab() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [settings, setSettings] = useState({
    create_endpoint: '',
    read_endpoint: '',
    order_head_read_query: '',
    order_line_read_query: '',
    order_line_read_query_columns: '',
    enter_by: '',
    warehouse: '',
    stat: '',
    rec_type: '',
    source_code: '',
    pay_amount: '',
    tot_code: '',
    auth_type: '',
    auth_avs: '',
    auth_memo: '',
    ord_class: '',
    ship_via_code: '',
    cu_po: '',
    print_where: '',
    disp_method: '',
    disp_where: '',
    free_form_shipto: '',
    terms_code: ''
  });

  // State for reset confirmation dialog
  const [showResetDialog, setShowResetDialog] = useState(false);

  const {
    data: settingsData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['orderSyncSettings'],
    queryFn: async () => {
      const response = await api.getOrderSyncSettings();
      return response.data.data;
    },
    staleTime: 0
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (updatedSettings) => {
      const response = await api.updateOrderSyncSettings(updatedSettings);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Order sync settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['orderSyncSettings'] });
    },
    onError: (error) => {
      toast.error(
        `Failed to update settings: ${error.response?.data?.message || error.message
        }`
      );
    }
  });

  // Reset settings mutation
  const resetSettingsMutation = useMutation({
    mutationFn: async () => {
      const response = await api.resetOrderSyncSettings();
      return response.data;
    },
    onSuccess: () => {
      toast.success('Order sync settings reset to default successfully');
      queryClient.invalidateQueries({ queryKey: ['orderSyncSettings'] });
      setShowResetDialog(false);
    },
    onError: (error) => {
      toast.error(`Failed to reset settings: ${error.response?.data?.message || error.message}`);
      setShowResetDialog(false);
    }
  });

  useEffect(() => {
    if (settingsData) {
      const processedSettings = {
        create_endpoint: settingsData.create_endpoint,
        read_endpoint: settingsData.read_endpoint,
        order_head_read_query: settingsData.order_head_read_query,
        order_line_read_query: settingsData.order_line_read_query,
        order_line_read_query_columns: settingsData.order_line_read_query_columns,
        enter_by: settingsData.enter_by,
        warehouse: settingsData.warehouse,
        stat: settingsData.stat,
        rec_type: settingsData.rec_type,
        source_code: settingsData.source_code,
        pay_amount: settingsData.pay_amount,
        tot_code: settingsData.tot_code,
        auth_type: settingsData.auth_type,
        auth_avs: settingsData.auth_avs,
        auth_memo: settingsData.auth_memo,
        ord_class: settingsData.ord_class,
        ship_via_code: settingsData.ship_via_code,
        cu_po: settingsData.cu_po,
        print_where: settingsData.print_where,
        disp_method: settingsData.disp_method,
        disp_where: settingsData.disp_where,
        free_form_shipto: settingsData.free_form_shipto,
        terms_code: settingsData.terms_code
      };

      setSettings(processedSettings);
    }
  }, [settingsData]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSettings((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Convert tot_code to array if it's a string
    const updatedSettings = {
      ...settings,
      tot_code:
        typeof settings.tot_code === 'string'
          ? settings.tot_code.split(',').map((code) => code.trim())
          : settings.tot_code
    };

    updateSettingsMutation.mutate(updatedSettings);
  };

  // Handle reset settings
  const handleResetSettings = () => {
    setShowResetDialog(true);
  };

  const handleConfirmReset = () => {
    resetSettingsMutation.mutate();
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity='error' sx={{ mb: 3 }}>
        Error loading settings: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: '15px' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={4}>
          <Box display='flex' alignItems='center'>
            <SettingsIcon color='primary' sx={{ mr: 1 }} />
            <Typography variant='h6'>
              Order Sync Settings
            </Typography>
          </Box>
          {isAdmin ? (
            <>
              <Button
                variant='contained'
                color='warning'
                onClick={handleResetSettings}
                disabled={!isAdmin || resetSettingsMutation.isPending}
                startIcon={<RestoreIcon />}
              >
                Reset
              </Button>
            </>
          ) : (<></>)}
        </Box>

        {!settingsData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Create Endpoint'
                  name='create_endpoint'
                  value={settings.create_endpoint}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='The endpoint used for creating orders in ERP'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Read Endpoint'
                  name='read_endpoint'
                  value={settings.read_endpoint}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='The endpoint used for reading orders from ERP'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Order Head Read Query'
                name='order_head_read_query'
                value={settings.order_head_read_query}
                onChange={handleInputChange}
                disabled={!isAdmin}
                helperText='Used to read order head from ERP'
                variant='outlined'
                sx={{ mb: 2 }}
                multiline
                rows={3}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Order Line Read Query'
                name='order_line_read_query'
                value={settings.order_line_read_query}
                onChange={handleInputChange}
                disabled={!isAdmin}
                helperText='Used to read order line from ERP'
                variant='outlined'
                sx={{ mb: 2 }}
                multiline
                rows={3}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Order Line Read Query Columns'
                name='order_line_read_query_columns'
                value={settings.order_line_read_query_columns}
                onChange={handleInputChange}
                disabled={!isAdmin}
                helperText='Comma-separated list of columns to fetch from ERP.'
                variant='outlined'
                sx={{ mb: 2 }}
                required
              />
            </Grid>

            <Typography variant='h6' color='primary' gutterBottom sx={{ mt: 4 }}>
            Default Values
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Enter By'
                  name='enter_by'
                  value={settings.enter_by}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default value for enter_by field'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Warehouse'
                  name='warehouse'
                  value={settings.warehouse}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default warehouse value'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Stat'
                  name='stat'
                  value={settings.stat}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default status value'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Rec Type'
                  name='rec_type'
                  value={settings.rec_type}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default record type value'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Source Code'
                  name='source_code'
                  value={settings.source_code}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default source code value'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Pay Amount'
                  name='pay_amount'
                  type='number'
                  step='0.01'
                  value={settings.pay_amount}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default payment amount'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Tot Code'
                  name='tot_code'
                  value={
                    Array.isArray(settings.tot_code)
                      ? settings.tot_code.join(', ')
                      : settings.tot_code
                  }
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Comma-separated list of tot codes (will be converted to array)'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Auth Type'
                  name='auth_type'
                  value={settings.auth_type}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default authorization type'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Auth AVS'
                  name='auth_avs'
                  value={settings.auth_avs}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default authorization AVS value'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Auth Memo'
                  name='auth_memo'
                  value={settings.auth_memo}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default authorization memo'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Ord Class'
                  name='ord_class'
                  value={settings.ord_class}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default order class'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Ship Via Code'
                  name='ship_via_code'
                  value={settings.ship_via_code}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default shipping via code'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='CU PO'
                  name='cu_po'
                  value={settings.cu_po}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default customer PO value'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Print Where'
                  name='print_where'
                  value={settings.print_where}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default print location'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Disp Method'
                  name='disp_method'
                  value={settings.disp_method}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default display method'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Disp Where'
                  name='disp_where'
                  value={settings.disp_where}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default display location'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Free Form Ship To'
                  name='free_form_shipto'
                  value={settings.free_form_shipto}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default free form ship to value'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Terms Code'
                  name='terms_code'
                  value={settings.terms_code}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default terms code for orders'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              {isAdmin ? (
                <>
                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                    startIcon={updateSettingsMutation.isPending ? <CircularProgress size={20} color='inherit' /> : <SaveIcon />}
                    disabled={!isAdmin || updateSettingsMutation.isPending }
                  >
                    {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                  </Button>
                </>
              ) : (<></>)}
            </Box>
          </form>
        )}
      </Paper>

      <ConfirmDialog
        open={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={handleConfirmReset}
        confirmColor='warning'
        title='Reset Settings to Default'
        message='Are you sure you want to reset all order sync settings to their default values? This action cannot be undone.'
        confirmText='Reset'
        cancelText='Cancel'
        severity='warning'
        isLoading={resetSettingsMutation.isPending}
      />
    </Box>
  );
}
